import React, { useEffect, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, TrashIcon, DownloadIcon, XIcon, CheckIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, PlusIcon, TicketIcon, SearchIcon } from 'lucide-react';
import { Campaign } from '../../../shared/types/Campaign';
import { TimeSelector } from '../../analytics/components/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  BILASHOP2025: {
    id: 'BILASHOP2025',
    title: '[BILA Mart] 7-days Promotion for Shop',
    description: '[BILA Mart] 7-days Promotion for Shop',
    expiryDate: '07/03/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILASHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    transactionVolume: 47250,
    transactionCount: 63
  },
  BILA10MYR: {
    id: 'BILA10MYR',
    title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/01/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILA10MYR',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active',
    transactionVolume: 1284500,
    transactionCount: 1096
  },
  BULK2025: {
    id: 'BULK2025',
    title: 'Summer Festival Bulk Codes',
    description: 'Unique codes for summer festival attendees',
    startDate: '01/11/2025',
    expiryDate: '31/01/2026',
    discountPercentage: 15,
    fixedDiscountAmount: 0,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 50000,
    budgetUsed: 12500,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'PromptPay'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: '',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Bulk',
    minTransactionValue: 500,
    maxTransactionValue: 999999,
    minDiscount: 0,
    maxDiscount: 1000,
    status: 'active',
    transactionVolume: 312750,
    transactionCount: 245
  }
};
// Mock Code Batches
const mockCodeBatches = [{
  id: 'BATCH-001',
  dateCreated: '15/12/2025',
  codesGenerated: 500,
  usageLimitPerCode: 1,
  codesUsed: 420,
  status: 'Active'
}, {
  id: 'BATCH-002',
  dateCreated: '20/12/2025',
  codesGenerated: 1000,
  usageLimitPerCode: 2,
  codesUsed: 150,
  status: 'Active'
}];
// Mock Individual Codes for a Batch
const mockIndividualCodes = [{
  code: 'BULK-A1B2C3D4',
  usedCount: 1,
  lastUsed: '25/12/2025 14:30',
  status: 'Fully Redeemed'
}, {
  code: 'BULK-E5F6G7H8',
  usedCount: 0,
  lastUsed: '-',
  status: 'Available'
}, {
  code: 'BULK-I9J0K1L2',
  usedCount: 1,
  lastUsed: '26/12/2025 09:15',
  status: 'Fully Redeemed'
}, {
  code: 'BULK-M3N4O5P6',
  usedCount: 0,
  lastUsed: '-',
  status: 'Available'
}, {
  code: 'BULK-Q7R8S9T0',
  usedCount: 1,
  lastUsed: '28/12/2025 18:45',
  status: 'Fully Redeemed'
}];
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  // Codes Tab State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(100);
  const [usageLimit, setUsageLimit] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-11640" data-mp="2gp5ncbuof0ko" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-11641" data-mp="2lp9wfgt98t0v" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-11642" data-mp="3s5c8i5e5knw1" />
  }];
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
      // Auto-select first batch for bulk campaigns
      if (mockCampaigns[campaignId].codeStrategy === 'Bulk' && mockCodeBatches.length > 0) {
        setSelectedBatch(mockCodeBatches[0].id);
      }
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    navigate('/campaigns');
  };
  const navigateToEdit = () => {
    if (campaign) {
      navigate(`/campaign-form?id=${campaign.id}`);
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  const handleGenerateCodes = () => {
    // Simulate generating codes
    console.log(`Generating ${generateCount} codes with limit ${usageLimit}`);
    setShowGenerateModal(false);
    // In a real app, this would refresh the batches list
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-11643" data-mp="3bxyyyooizvoe">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const getCodeStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Available: 'bg-green-50 text-green-700 border border-green-100',
      'Fully Redeemed': 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    const style = styles[status] || styles['Available'];
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-11644" data-mp="2s9992c3opsk1">
        {status}
      </span>;
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-11645" data-mp="2iatgs5sx8d8y">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2231A]" data-id="element-11646" data-mp="3fx0ldfp865lb"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-2xl mx-auto mt-12" data-id="element-11647" data-mp="3azqwc15qzvy0">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11648" data-mp="317um5rqe0fdf">
          {t('pages.CampaignDetails.campaign_not_found')}
        </h2>
        <p className="text-gray-500 mb-6" data-id="element-11649" data-mp="2iw4rhuehxz8">
          {t('pages.CampaignDetails.the_campaign_you_are_looking_for')}
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11650" data-mp="3tv7aa6c74v8u">
          {t('pages.CampaignDetails.back_to_campaigns')}
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-11651" data-mp="o1oa75ig1hk8">
            <div data-id="element-11652" data-mp="nzahv4f5ejgd">
              <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11653" data-mp="szy6xkjtgq7y">
                {t('pages.renderAnalyticsContent.conversion_funnel')}
              </h3>
              <ConversionFunnelChart data-id="element-11654" data-mp="xtajgwb816kc" />
            </div>
            <div data-id="element-11655" data-mp="12opulghugod2">
              <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11656" data-mp="12p9c4w3l1aqk">
                {t('pages.renderAnalyticsContent.conversion_rates')}
              </h3>
              <ConversionRatePieChart data-id="element-11657" data-mp="1brinfo6od850" />
            </div>
          </div>;
      case 'usage':
        return <div data-id="element-11658" data-mp="3h6t4qh6zuka9">
            <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11659" data-mp="3h35bzk9di1wl">
              {t('pages.renderAnalyticsContent.code_usage_over_time_last_30')}
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-11660" data-mp="3r12z24fhxsnm" />
          </div>;
      case 'volume':
        return <div data-id="element-11661" data-mp="3vvyz9ruvzmbt">
            <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11662" data-mp="4nl718czmme6">
              {t('pages.renderAnalyticsContent.payment_volume_over_time_last_30')}
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-11663" data-mp="dnztwc5lo9js" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-11664" data-mp="il9sahayvvjr">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-11665" data-mp="2fh78tkhawp55">
        <div className="flex items-center gap-4" data-id="element-11666" data-mp="2ke7ehua8npvq">
          <button onClick={navigateBack} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-11667" data-mp="2kezctcv801pp">
            <ArrowLeftIcon size={20} data-id="element-11668" data-mp="283gdi3lln2yd" />
          </button>
          <div data-id="element-11669" data-mp="235eneypra3de">
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3" data-id="element-11670" data-mp="234vg0bxqexdm">
              {campaign.title}
              {getStatusBadge(campaign.status)}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-11671" data-mp="2vtslfegugzoy">
              {t('pages.CampaignDetails.campaign_id')} {campaign.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-11672" data-mp="2qswib34bhhkl">
          <button onClick={() => setShowExportModal(true)} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-11673" data-mp="2lv3eggqhkznh">
            <DownloadIcon size={16} className="mr-2" data-id="element-11674" data-mp="1aed2egzdtdly" />
            {t('pages.CampaignDetails.export_csv')}
          </button>
          <button onClick={navigateToEdit} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-11675" data-mp="3tnv0gvap4w0r">
            <EditIcon size={16} className="mr-2" data-id="element-11676" data-mp="3jovytwocm0n9" />
            {t('pages.CampaignDetails.edit')}
          </button>
          <button className="flex items-center px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors shadow-sm" data-id="element-11677" data-mp="3omnrwu0qbst1">
            <TrashIcon size={16} className="mr-2" data-id="element-11678" data-mp="3er47b3rm8gjl" />
            {t('pages.CampaignDetails.delete')}
          </button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-id="element-11679" data-mp="35ysfbdgusl1i">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200" data-id="element-11680" data-mp="2r19vmpj18y0x">
            <div className="flex justify-between items-center mb-6" data-id="element-11681" data-mp="2vz1mrhbusg42">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-11682" data-mp="2cf2qtfvsor0c">
                {t('pages.CampaignDetails.export_campaign_data')}
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-11683" data-mp="28esnyc3augpp">
                <XIcon size={20} aria-hidden="true" data-id="element-11684" data-mp="2vastl5bjxrfa" />
              </button>
            </div>
            <div className="mb-6" data-id="element-11685" data-mp="2r97my1pm29dj">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-11686" data-mp="352fbfkieaor0">
                {t('pages.CampaignDetails.select_time_period_for_export')}
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-11687" data-mp="305p01ahjchbx" />
            </div>
            <div className="mb-8 bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-11688" data-mp="3a2bgln2twm85">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-11689" data-mp="17fe8boarqbba">
                {t('pages.CampaignDetails.the_export_will_include')}
              </p>
              <ul className="space-y-1.5 text-sm text-gray-600" data-id="element-11690" data-mp="1ccehi82wzu6m">
                <li className="flex items-center gap-2" data-id="element-11691" data-mp="1h4o53hgk4i5q">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-11692" data-mp="1h7ac9fzjcvnf" />
                  {t('pages.CampaignDetails.daily_code_usage_statistics')}
                </li>
                <li className="flex items-center gap-2" data-id="element-11693" data-mp="ohtlsnvh7otu">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-11694" data-mp="tetpis4usfhs" />
                  {t('pages.CampaignDetails.conversion_metrics')}
                </li>
                <li className="flex items-center gap-2" data-id="element-11695" data-mp="ygj42hrabbam">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-11696" data-mp="13dszce1cuh6d" />
                  {t('pages.CampaignDetails.transaction_volumes_and_amounts')}
                </li>
                <li className="flex items-center gap-2" data-id="element-11697" data-mp="2esohi9z7n0ro">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-11698" data-mp="2er50ui9n6npd" />
                  {t('pages.CampaignDetails.user_engagement_data')}
                </li>
              </ul>
            </div>
            <div className="flex justify-end gap-3" data-id="element-11699" data-mp="3uc5vcxfko64a">
              <button onClick={() => setShowExportModal(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-11700" data-mp="3pknue9dju5jb">
                {t('pages.CampaignDetails.cancel')}
              </button>
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-11701" data-mp="3bn91guo37fml">
                  <DownloadIcon size={16} className="mr-2" data-id="element-11702" data-mp="14uyfjwcuijqm" />
                  {t('pages.CampaignDetails.export_data')}
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-[#E2231A] text-white text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed" data-id="element-11703" data-mp="2ubkai5874ud6">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-900/20 border-t-gray-900 mr-2" data-id="element-11704" data-mp="2kez0t6l8ev30"></div>
                  {t('pages.CampaignDetails.exporting')}
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-[#FEE7E5] text-gray-900 text-sm font-medium rounded-lg flex items-center cursor-not-allowed" data-id="element-11705" data-mp="3i1y0lq780j54">
                  <CheckIcon size={16} className="mr-2" data-id="element-11706" data-mp="382fkxlycfjiv" />
                  {t('pages.CampaignDetails.export_complete')}
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-11707" data-mp="1wobpebc3xnbp">
                  {t('pages.CampaignDetails.try_again')}
                </button>}
            </div>
          </div>
        </div>}

      {/* Generate Codes Modal */}
      {showGenerateModal && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-id="element-11708" data-mp="jnp4cn1zuc6l">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200" data-id="element-11709" data-mp="3n68muxuve3o7">
            <div className="flex justify-between items-center mb-6" data-id="element-11710" data-mp="4s96e6yalw4x">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-11711" data-mp="4tka66rqn3w1">
                {t('pages.CampaignDetails.generate_code_batch')}
              </h3>
              <button onClick={() => setShowGenerateModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-11712" data-mp="38gkj7pz7usxb">
                <XIcon size={20} aria-hidden="true" data-id="element-11713" data-mp="3tbl4jf0g0zse" />
              </button>
            </div>

            <div className="space-y-4 mb-8" data-id="element-11714" data-mp="259y13cnz6to">
              <div data-id="element-11715" data-mp="26cr9hxgllcr">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11716" data-mp="663mbxu0qnn3">
                  {t('pages.CampaignDetails.number_of_codes_to_generate')}
                </label>
                <input type="number" min="1" max="10000" value={generateCount} onChange={e => setGenerateCount(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-11717" data-mp="b2ka2qxb2zzn" />
              </div>
              <div data-id="element-11718" data-mp="yw2gg7n2laym">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11719" data-mp="ysem569swkiu">
                  {t('pages.CampaignDetails.usage_limit_per_code')}
                </label>
                <input type="number" min="1" value={usageLimit} onChange={e => setUsageLimit(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-11720" data-mp="1dybyzk2n93xy" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-11721" data-mp="1woa5vwupgr00">
                  {t('pages.CampaignDetails.how_many_times_each_individual_code')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3" data-id="element-11722" data-mp="1mwe8yp4cv6xd">
              <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-11723" data-mp="1ix5bpaa3wqx2">
                {t('pages.CampaignDetails.cancel')}
              </button>
              <button onClick={handleGenerateCodes} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-11724" data-mp="5415b8wrhlb6">
                {t('pages.CampaignDetails.generate_codes')}
              </button>
            </div>
          </div>
        </div>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11725" data-mp="opxjthzky33f">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11726" data-mp="jrm7n0ltbgi1">
          <div className="flex justify-between items-start mb-2" data-id="element-11727" data-mp="f1fhuxfkdpy7">
            <div className="text-sm font-medium text-gray-500" data-id="element-11728" data-mp="ezl0tu7eyhze">
              {t('components.CampaignList.budget_usage')}
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-11729" data-mp="17hgkvja1n72t">
              {calculateBudgetUsage()}%
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4 overflow-hidden" data-id="element-11730" data-mp="12q89ihp5lbn0">
            <div className="bg-[#E2231A] h-1.5 rounded-full transition-all" style={{
            width: `${calculateBudgetUsage()}%`
          }} data-id="element-11731" data-mp="1a37r62r7rkrs"></div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11732" data-mp="1jyrazqsi6t06">
            {campaign.budgetUsed.toLocaleString('en-US')}
            <span className="text-sm font-medium text-gray-500 ml-1" data-id="element-11733" data-mp="1oxcbzsfkxgpo">
              / {campaign.budget.toLocaleString('en-US')} {campaign.currency}
            </span>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11734" data-mp="1xy0qspcsrl3m">
          <div className="text-sm font-medium text-gray-500" data-id="element-11735" data-mp="22rbx8pdp5zv0">
            {t('pages.CampaignDetails.transaction_volume')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-11736" data-mp="2cozyly5t65m5">
            {campaign.currency}{' '}
            {campaign.transactionVolume?.toLocaleString('en-US') || '0.00'}
          </div>
          <div className="mt-1 text-sm text-gray-500" data-id="element-11737" data-mp="hluv2teexmpu">
            {t('pages.CampaignDetails.from')} {campaign.transactionCount || 0}{' '}
            {t('pages.CampaignDetails.transactions')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11738" data-mp="qeew20i6g3du">
          <div className="text-sm font-medium text-gray-500" data-id="element-11739" data-mp="lnopijy0luhk">
            {t('pages.CampaignDetails.volume_roi')}
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-11740" data-mp="10b9vqmq6s9tp">
            {campaign.budget > 0 ? `${((campaign.transactionVolume || 0) / campaign.budget).toFixed(1)}x` : '0x'}
          </div>
          <div className="mt-1 text-sm text-gray-500" data-id="element-11741" data-mp="19c7yl1ow2bap">
            {t('pages.CampaignDetails.against_budget_of')} {campaign.currency}{' '}
            {campaign.budget.toLocaleString('en-US')}
          </div>
        </div>
      </div>

      {/* Main Content Card - Details */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11742" data-mp="3743voqnyj9yf">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-11743" data-mp="2wzwy0ypa6vpr">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-11744" data-mp="310p9zzpjfepq">
            {t('pages.CampaignDetails.campaign_details')}
          </h2>
        </div>
        <div className="p-6" data-id="element-11745" data-mp="2s7vohkjhzj3f">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8" data-id="element-11746" data-mp="2s73taattbndo">
            <div data-id="element-11747" data-mp="2dad3mt9xxq19">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11748" data-mp="2d9tiz4swonjj">
                {t('pages.CampaignDetails.category')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11749" data-mp="2i7v4dwdt0yy6">{campaign.category}</div>
            </div>
            <div data-id="element-11750" data-mp="24c9ihy2vav9z">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11751" data-mp="2999o67vt1w0k">
                {t('components.CampaignList.code_strategy')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11752" data-mp="2k4pskdjnty7">
                {campaign.codeStrategy}
              </div>
            </div>
            <div data-id="element-11753" data-mp="7fk4fejdltvo">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11754" data-mp="23ksw9256er18">
                {t('pages.CampaignDetails.campaign_code')}
              </div>
              <div className="text-sm text-gray-900 font-medium" data-id="element-11755" data-mp="1ymhmssismijj">
                {campaign.code || 'N/A'}
              </div>
            </div>
            <div data-id="element-11756" data-mp="1pla077pn365a">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11757" data-mp="1plt8dwphmg64">
                {t('components.CampaignList.expiry_date')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11758" data-mp="1knhyxn33u7of">{campaign.expiryDate}</div>
            </div>
            <div data-id="element-11759" data-mp="1fu5nkenfrxdm">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11760" data-mp="1ftnf8jni1spc">
                {t('components.CampaignList.discount')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11761" data-mp="15yc225rqotjp">
                {campaign.discountPercentage > 0 ? `${campaign.discountPercentage}%` : `${campaign.fixedDiscountAmount} ${campaign.currency}`}
              </div>
            </div>
            <div data-id="element-11762" data-mp="38k7wjsbeg3v5">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11763" data-mp="38kr36cvln5tv">
                {t('pages.CampaignDetails.discount_carried_by')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11764" data-mp="3dkvnkdzmokpd">
                {campaign.discountCarriedBy}
                {campaign.merchantPercentage ? ` (${campaign.merchantPercentage}% Merchant)` : ''}
              </div>
            </div>
            <div className="md:col-span-3" data-id="element-11765" data-mp="2pv1b3xj80y5n">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11766" data-mp="2tv1rhnpvrjfc">
                {t('pages.CampaignDetails.description')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11767" data-mp="2ytd1pzk37vy3">
                {campaign.description || 'No description provided.'}
              </div>
            </div>
          </div>
        </div>

        {/* Constraints & Targeting Section */}
        <div className="p-6 border-t border-gray-100" data-id="element-11768" data-mp="263msa3tehi8b">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-11769" data-mp="2b397gissh8jr">
            {t('pages.CampaignDetails.constraints_targeting')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8" data-id="element-11770" data-mp="39gwo8nwcw8kt">
            <div data-id="element-11771" data-mp="3n7rxw2eoitpg">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11772" data-mp="3n8b62rej23qa">
                {t('pages.CampaignDetails.start_date')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11773" data-mp="3j6zon95t4ot6">
                {campaign.startDate || 'Not set'}
              </div>
            </div>
            <div data-id="element-11774" data-mp="2qhse1bt2w8rk">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11775" data-mp="2qiakt2dda5dq">
                {t('pages.CampaignDetails.transaction_value_limits')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11776" data-mp="34exjb9kyx3lr">
                {campaign.currency}{' '}
                {campaign.minTransactionValue.toLocaleString()} –{' '}
                {campaign.currency}{' '}
                {campaign.maxTransactionValue.toLocaleString()}
              </div>
            </div>
            <div data-id="element-11777" data-mp="n908dwdcgjcy">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11778" data-mp="n8gyn2xul1a0">
                {t('pages.CampaignDetails.discount_limits')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11779" data-mp="rbww21hqr5nr">
                {campaign.currency} {campaign.minDiscount.toLocaleString()} –{' '}
                {campaign.currency} {campaign.maxDiscount.toLocaleString()}
              </div>
            </div>
            <div data-id="element-11780" data-mp="15yygudl20eh4">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11781" data-mp="160jbs4ba5zfi">
                {t('pages.CampaignDetails.valid_days')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11782" data-mp="1b0dyrwrxq4va">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </div>
            </div>
            <div data-id="element-11783" data-mp="1oym0ehzx1ucs">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11784" data-mp="1oy2njfp4hw5m">
                {t('pages.CampaignDetails.payment_methods')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11785" data-mp="1tqvqby116uf2">
                {campaign.paymentMethods && campaign.paymentMethods.length > 0 ? campaign.paymentMethods.join(', ') : 'All payment methods'}
              </div>
            </div>
            <div data-id="element-11786" data-mp="3g2xeq6bniln6">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11787" data-mp="3g0b7y8wl4a60">
                {t('pages.CampaignDetails.budget_enforcement')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11788" data-mp="clxefrnzrf9z">
                {campaign.strictlyEnforceBudget ? 'Strictly enforced' : 'Soft limit'}
              </div>
            </div>
            <div data-id="element-11789" data-mp="3jo3x1sq3lwl">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11790" data-mp="3j5ypfo31xcj">
                {t('pages.CampaignDetails.budget_type')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11791" data-mp="2obrabdowe1s3">
                {campaign.budgetType === 'recurring' ? `Recurring (${campaign.recurringBudgetPeriod || 'monthly'})` : 'Fixed'}
              </div>
            </div>
            <div data-id="element-11792" data-mp="yvxcwl6pfyyg">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11793" data-mp="ys9kxqgwrklu">
                {t('pages.CampaignDetails.customer_usage_limits')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11794" data-mp="17tzbyv6vohj3">
                {campaign.limitUsage ? `Limited to: ${campaign.limitUsage}` : 'No usage limits'}
              </div>
            </div>
            <div data-id="element-11795" data-mp="1hkc170pn0yvk">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-11796" data-mp="1hmye7fvas6l6">
                {t('pages.CampaignDetails.card_targeting')}
              </div>
              <div className="text-sm text-gray-900" data-id="element-11797" data-mp="1rk2zzel8e01g">
                {campaign.paymentMethods?.includes('Card') ? (() => {
                const rules: string[] = [];
                if (campaign.cardBrands && campaign.cardBrands.length > 0) rules.push(`Brands: ${campaign.cardBrands.join(', ')}`);
                if (campaign.cardType && campaign.cardType !== 'All Types') rules.push(`Type: ${campaign.cardType}`);
                if (campaign.cardBin) rules.push(`BINs: ${campaign.cardBin}`);
                if (campaign.cardIssuingCountry) rules.push(`Country: ${campaign.cardIssuingCountry}`);
                return rules.length > 0 ? rules.join(' • ') : 'All cards accepted';
              })() : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Analytics Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11798" data-mp="1f72vstst8sz">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-11799" data-mp="2h6l8nxc16o4i">
          <div className="flex items-center gap-2" data-id="element-11800" data-mp="2m5683ujgl3rw">
            <PieChartIcon size={20} className="text-gray-400" data-id="element-11801" data-mp="2dp78durp673s" />
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-11802" data-mp="29onbmsb1d5i6">
              {t('pages.CampaignDetails.analytics')}
            </h2>
          </div>

          {/* Analytics Tabs */}
          <div className="flex space-x-6 overflow-x-auto" role="tablist" aria-label="Campaign analytics tabs" data-id="element-11803" data-mp="2ipl8mqn24cr9">
            {analyticsTabs.map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls="campaign-analytics-tabpanel" id={`campaign-analytics-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11804" data-mp="1q165p2h79kwa">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
        </div>

        <div className="p-6" data-id="element-11805" data-mp="3h27gm5tsoik3">{renderAnalyticsContent()}</div>
      </div>

      {/* Code Batches Section - Standalone for Bulk campaigns */}
      {campaign.codeStrategy === 'Bulk' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11806" data-mp="3l5nc2yu4ocv7">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-11807" data-mp="2ofwir2xkovqz">
            <div className="flex items-center gap-2" data-id="element-11808" data-mp="2ter163rmoiak">
              <TicketIcon size={20} className="text-gray-400" data-id="element-11809" data-mp="2xd6v6rm4mqxh" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-11810" data-mp="32dbe0oaibxqv">
                {t('pages.CampaignDetails.code_batches')}
              </h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-11811" data-mp="cnrb0fhey0gt">
                {mockCodeBatches.length} {t('pages.CampaignDetails.batches')}
              </span>
            </div>
            <button onClick={() => setShowGenerateModal(true)} className="flex items-center px-3 py-1.5 bg-[#E2231A] text-white text-sm font-medium rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm" data-id="element-11812" data-mp="rt5211hhjbcm">
              <PlusIcon size={16} className="mr-1.5" data-id="element-11813" data-mp="e0fgey5vc8g1" />
              {t('pages.CampaignDetails.generate_batch')}
            </button>
          </div>

          {/* Batch Summary Cards */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/30" data-id="element-11814" data-mp="40wxml1p2spk">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-11815" data-mp="43swcbld9lf2">
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-11816" data-mp="3v89f716hp860">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-11817" data-mp="3r77d8cl6ui0c">
                  {t('pages.CampaignDetails.total_codes')}
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-11818" data-mp="1pet0rt1ey5rb">
                  {mockCodeBatches.reduce((sum, b) => sum + b.codesGenerated, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-11819" data-mp="21frd1hcs2i6i">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-11820" data-mp="1ni2o9yflaqw6">
                  {t('pages.CampaignDetails.total_redeemed')}
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-11821" data-mp="1rkr1sjf90lzh">
                  {mockCodeBatches.reduce((sum, b) => sum + b.codesUsed, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-11822" data-mp="13ud68ixgg505">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-11823" data-mp="18rviai6rr0cd">
                  {t('pages.CampaignDetails.redemption_rate')}
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-11824" data-mp="uuqe6nqm8bjr">
                  {Math.round(mockCodeBatches.reduce((sum, b) => sum + b.codesUsed, 0) / mockCodeBatches.reduce((sum, b) => sum + b.codesGenerated, 0) * 100)}
                  %
                </div>
              </div>
            </div>
          </div>

          {/* Batches Table */}
          <div className="overflow-x-auto" data-id="element-11825" data-mp="l7dz6zzritcs">
            <table className="w-full text-left border-collapse" data-id="element-11826" data-mp="z3r99o1yvsh8">
              <thead data-id="element-11827" data-mp="u5zgkrti02bz">
                <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-11828" data-mp="u6hlsdy51qw1">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11829" data-mp="18ucg1yrso3b8">
                    {t('pages.CampaignDetails.batch_id')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11830" data-mp="13xvikdypgclh">
                    {t('pages.CampaignDetails.date_created')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11831" data-mp="1cu4hzsmonb7m">
                    {t('pages.CampaignDetails.codes_generated')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11832" data-mp="fi4eh53q4fkn">
                    {t('pages.CampaignDetails.usage_limit')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11833" data-mp="obr6ql8q30p3">
                    {t('pages.CampaignDetails.redemption')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11834" data-mp="y72hksh0fnrk">
                    {t('components.CampaignList.status')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11835" data-mp="1780phlukkfff">
                    {t('pages.CampaignDetails.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-11836" data-mp="390ou9zmv3axb">
                {mockCodeBatches.map(batch => <tr key={batch.id} onClick={() => setSelectedBatch(selectedBatch === batch.id ? null : batch.id)} className={`cursor-pointer transition-colors ${selectedBatch === batch.id ? 'bg-[#FEE7E5]/50' : 'hover:bg-gray-50'}`} data-id="element-11837" data-mp="31iqredz6qy32">
                    <td className="px-6 py-4" data-id="element-11838" data-mp="3fgnpjetafcju">
                      <span className="text-sm font-medium text-gray-900 font-mono" data-id="element-11839" data-mp="3fhqcjbnhouuh">
                        {batch.id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-11840" data-mp="36gigz1ccy843">
                      {batch.dateCreated}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium" data-id="element-11841" data-mp="201hvjpkyl8br">
                      {batch.codesGenerated.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-11842" data-mp="3v2jvf7ia3zkv">
                      {batch.usageLimitPerCode}
                      {t('pages.CampaignDetails.per_code')}
                    </td>
                    <td className="px-6 py-4" data-id="element-11843" data-mp="3l5p1l1wyancx">
                      <div className="flex items-center gap-3" data-id="element-11844" data-mp="3l704kziknr2z">
                        <div className="w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden" data-id="element-11845" data-mp="3q51lbdsizeba">
                          <div className="bg-blue-500 h-1.5 rounded-full" style={{
                      width: `${batch.codesUsed / batch.codesGenerated * 100}%`
                    }} data-id="element-11846" data-mp="hp857023w4ur" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium" data-id="element-11847" data-mp="cvvtfqiizsjf">
                          {batch.codesUsed}/{batch.codesGenerated}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-11848" data-mp="15fuaijkq7b12">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-11849" data-mp="2q3dd2nn1ci90">
                        {batch.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-11850" data-mp="2yyb75y1x611x">
                      <button className="text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-11851" data-mp="2uw6f33r4dche">
                        <DownloadIcon size={14} data-id="element-11852" data-mp="2763qwyqbsqo2" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Expanded Individual Codes for selected batch */}
          {selectedBatch && <div className="border-t border-gray-200" data-id="element-11853" data-mp="2b64f3b388g8b">
              <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-11854" data-mp="3rl5myhnskc92">
                <div data-id="element-11855" data-mp="3ml9jqkmx4ouk">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-11856" data-mp="3mo5i2a2ohfjj">
                    {t('pages.CampaignDetails.individual_codes')}{' '}
                    {selectedBatch}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-11857" data-mp="3b45mv5bfrrh">
                    {mockIndividualCodes.length}{' '}
                    {t('pages.CampaignDetails.codes_shown')}{' '}
                    {mockIndividualCodes.filter(c => c.status === 'Available').length}{' '}
                    {t('pages.CampaignDetails.available')}{' '}
                    {mockIndividualCodes.filter(c => c.status === 'Fully Redeemed').length}{' '}
                    {t('pages.CampaignDetails.redeemed')}
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-11858" data-mp="15rxl873shane">
                  <div className="relative" data-id="element-11859" data-mp="15qw0fawz19sh">
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11860" data-mp="1db6kv8mhtuz9" />

                    <input type="search" aria-label={t('pages.CampaignDetails.attrs.search_code')} placeholder={t('pages.CampaignDetails.attrs.search_code')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] bg-white" data-id="element-11861" data-mp="1i7wzdringiik" />
                  </div>
                  <button className="flex items-center px-3 py-1.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-white transition-colors bg-white" data-id="element-11862" data-mp="1r9n0jp2c2vtj">
                    <DownloadIcon size={14} className="mr-1.5" data-id="element-11863" data-mp="2jrij19pbfcuu" />
                    {t('pages.CampaignDetails.export_codes')}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-11864" data-mp="2pl45h4cu3f74">
                <table className="w-full text-left border-collapse" data-id="element-11865" data-mp="2pmff3ksynh65">
                  <thead className="bg-gray-50/50" data-id="element-11866" data-mp="2klhrutt3fwxs">
                    <tr data-id="element-11867" data-mp="2fnge8oefsptp">
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-11868" data-mp="2fo9b8xnlg0yn">
                        {t('pages.CampaignDetails.code')}
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-11869" data-mp="25wlnb6q8x21x">
                        {t('pages.CampaignDetails.times_used')}
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-11870" data-mp="1wyt802kixuej">
                        {t('pages.CampaignDetails.last_used')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-11871" data-mp="1n154oo8urekr">
                        {t('components.CampaignList.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-11872" data-mp="1mqyrq6hxye5x">
                    {mockIndividualCodes.filter(code => !searchTerm || code.code.toLowerCase().includes(searchTerm.toLowerCase())).map((code, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-11873" data-mp="18ta8t47fph3i" data-mpi={idx}>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 font-mono" data-id="element-11874" data-mp="18ulcz54skqv5">
                            {code.code}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500" data-id="element-11875" data-mp="gbxw61wtvxzv">
                            {code.usedCount}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500" data-id="element-11876" data-mp="2d326c1y9qqm0">
                            {code.lastUsed}
                          </td>
                          <td className="px-6 py-3 text-right" data-id="element-11877" data-mp="2rsq8t6i6rvbb">
                            {getCodeStatusBadge(code.status)}
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>}
    </div>;
}